import { useState, useCallback } from 'react'

interface ValidationRule {
    required: boolean
    validation?: (value: string) => string | null
}

export interface ValidationRules {
    [key: string]: ValidationRule
}

export interface ValidationErrors {
    [key: string]: string | null
}

export const useFormValidation = (rules: ValidationRules) => {
    const [errors, setErrors] = useState<ValidationErrors>({})

    const validateField = useCallback((name: string, value: string): string | null => {
        const rule = rules[name] || null

        if (rule && rule.validation) {
            if (!value && !rule.required) {
                return null
            }

            return rule.validation(value)
        }

        return null
    }, [rules])

    const validateAllFields = useCallback((data: Record<string, string>): boolean => {
        const newErrors: ValidationErrors = {}

        Object.keys(rules).forEach(fieldName => {
            const error = validateField(fieldName, data[fieldName] || '')

            if (error !== null) {
                newErrors[fieldName] = error
            }
        })

        setErrors(newErrors)

        return !Object.keys(newErrors).length
    }, [rules, validateField])

    const validateSingleField = useCallback((name: string, value: string) => {
        const error = validateField(name, value)

        setErrors(prev => ({ ...prev, [name]: error }))

        return !error
    }, [validateField])

    const setError = (field: string, message: string) => {
        setErrors(prev => ({ ...prev, [field]: message }))
    }

    const removeError = (field: string) => {
        setErrors(prevErrors => {
            delete prevErrors[field]
            return { ...prevErrors }
        })
    }

    const clearErrors = useCallback(() => {
        setErrors({})
    }, [])

    return {
        errors,
        validateAllFields,
        validateSingleField,
        setError,
        removeError,
        clearErrors,
    }
}
